import axios from '../utils/axios';

const API_URL = `${process.env.REACT_APP_API_URL}/v1/client/`;

const bookingsUrl = (clinicId) => `${API_URL}clinics/${clinicId}/bookings`;

const buildAppointmentRequestParams = (pageNo, pageSize, params = {}) => {
    const searchParams = new URLSearchParams({
        pageNo: String(pageNo),
        pageSize: String(pageSize),
        status: String(params.status ?? 5),
    });

    if (params.column && params.value?.trim()) {
        searchParams.set('column', params.column);
        searchParams.set('value', params.value.trim());
    } else {
        searchParams.set('column', 'all');
        searchParams.set('value', 'value');
    }

    return searchParams.toString();
};

/** GET /bookings/appt-requests */
const getBookingRequests = (clinicId, pageNo = 0, pageSize = 50, params = {}) => {
    const query = buildAppointmentRequestParams(pageNo, pageSize, params);
    return axios.get(`${bookingsUrl(clinicId)}/appt-requests?${query}`);
};

/** Legacy helper: accepts a pre-built query string like "status=0&column=firstName&value=John" */
const getBookingRequestsByQuery = (clinicId, pageNo = 0, pageSize = 50, query = '') => {
    const prefix = `pageNo=${pageNo}&pageSize=${pageSize}`;
    const suffix = query ? `&${query}` : '';
    return axios.get(`${bookingsUrl(clinicId)}/appt-requests?${prefix}${suffix}`);
};

/** PUT /bookings/appt-requests/{apptRequestId} */
const updateBookingRequests = (clinicId, data) =>
    axios.put(`${bookingsUrl(clinicId)}/appt-requests/${data.id}`, data);

/** GET /bookings/doctors */
const getDoctorList = (clinicId) => axios.get(`${bookingsUrl(clinicId)}/doctors`);

/** GET /bookings/doctors/{doctorId} */
const getDoctorDetails = (clinicId, doctorId) =>
    axios.get(`${bookingsUrl(clinicId)}/doctors/${doctorId}`);

/** PUT /bookings/doctors/{doctorId} */
const updateDoctorDetails = (clinicId, doctorId, data) =>
    axios.put(`${bookingsUrl(clinicId)}/doctors/${doctorId}`, data);

/** GET /bookings/doctors/{doctorId}/hours */
const getDoctorAvailability = (clinicId, doctorId) =>
    axios.get(`${bookingsUrl(clinicId)}/doctors/${doctorId}/hours`);

/** PUT /bookings/doctors/{doctorId}/hours */
const updateDoctorAvailability = (clinicId, doctorId, data) =>
    axios.put(`${bookingsUrl(clinicId)}/doctors/${doctorId}/hours`, data);

/** GET /bookings/doctors/{doctorId}/custom-hours */
const getCustomHours = (clinicId, doctorId, pageNo = 0, pageSize = 50) =>
    axios.get(`${bookingsUrl(clinicId)}/doctors/${doctorId}/custom-hours?pageNo=${pageNo}&pageSize=${pageSize}`);

/** POST /bookings/doctors/{doctorId}/custom-hours */
const addCustomHour = (clinicId, doctorId, data) =>
    axios.post(`${bookingsUrl(clinicId)}/doctors/${doctorId}/custom-hours`, data);

/** DELETE /bookings/doctors/{doctorId}/custom-hours/{customHoursId} */
const deleteCustomHour = (clinicId, doctorId, customHourId) =>
    axios.delete(`${bookingsUrl(clinicId)}/doctors/${doctorId}/custom-hours/${customHourId}`);

/** GET /bookings/appt-types */
const getAllAppointmentTypes = (clinicId) => axios.get(`${bookingsUrl(clinicId)}/appt-types`);

/** POST /bookings/appt-types */
const addUpdateAppointmentType = (clinicId, data) =>
    axios.post(`${bookingsUrl(clinicId)}/appt-types`, data);

/** DELETE /bookings/appt-types/{apptTypeId} */
const deleteAppointmentTypeById = (clinicId, apptTypeId) =>
    axios.delete(`${bookingsUrl(clinicId)}/appt-types/${apptTypeId}`);

/** GET /bookings/doctors/{doctorId}/appt-types */
const getDoctorAppointmentTypes = (clinicId, doctorId) =>
    axios.get(`${bookingsUrl(clinicId)}/doctors/${doctorId}/appt-types`);

/** POST /bookings/doctors/{doctorId}/appt-types */
const addUpdateDoctorAppointmentTypes = (clinicId, doctorId, apptTypeIds) =>
    axios.post(`${bookingsUrl(clinicId)}/doctors/${doctorId}/appt-types`, apptTypeIds);

/** DELETE /bookings/doctors/{doctorId}/appt-types */
const deleteDoctorAppointmentTypes = (clinicId, doctorId, apptTypeIds) =>
    axios.delete(`${bookingsUrl(clinicId)}/doctors/${doctorId}/appt-types`, { data: apptTypeIds });

const bookingService = {
    getBookingRequests,
    getBookingRequestsByQuery,
    updateBookingRequests,
    getDoctorList,
    getDoctorDetails,
    updateDoctorDetails,
    getDoctorAvailability,
    updateDoctorAvailability,
    getCustomHours,
    addCustomHour,
    deleteCustomHour,
    getAllAppointmentTypes,
    addUpdateAppointmentType,
    deleteAppointmentTypeById,
    getDoctorAppointmentTypes,
    addUpdateDoctorAppointmentTypes,
    deleteDoctorAppointmentTypes,
    // Backward-compatible aliases
    getDoctorCustomWorkingHours: getCustomHours,
    addUpdateDoctorCustomWorkingHours: addCustomHour,
};

export default bookingService;
